// Client HTTP de l'API famille : fetch MÊME ORIGINE, JSON, cookie de session inclus.
// Chaque fonction traduit une route de server/ (voir shared/api-types.ts pour les formes).
// Contrat d'erreur : sur réponse non 2xx, on LÈVE Error(message français du serveur)
// pour que l'UI l'affiche tel quel ; apiMe() est la seule exception (401 = déconnecté,
// donc null plutôt qu'une erreur). Une PANNE RÉSEAU (serveur absent, ex. dev:web) fait
// rejeter fetch : l'erreur remonte et main.ts bascule en mode hors ligne.
import type { ParcoursDto } from './parcours-ui'
import type { OverrideDto } from './overrides'
import type { CampaignFull, CampaignScoreResponse, CampaignSummary, CrackResponse, EquipResponse, LeaderboardResponse, LevelFull, DuelsResponse, GhostResponse, GhostRivalDto, GhostRivalsResponse, JournalResponse, LevelSummary, MatchesResponse, MeResponse, MineStateResponse, QuestProgressResponse, ScoreResponse, WalletDto } from '../shared/api-types'
import type { QuestCounters } from '../shared/economy'
import type { MonkSpeed } from '../shared/types'
import type { WorldKey } from '../shared/worlds'

/** Appel bas niveau : préfixe /api, cookies de session, corps JSON optionnel. */
async function request(path: string, method: string, body?: unknown): Promise<Response> {
  const init: RequestInit = { method, credentials: 'same-origin' }
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' }
    init.body = JSON.stringify(body)
  }
  return fetch('/api' + path, init)
}

/** Erreur d'API qui PORTE son statut HTTP. La file des chronos hors ligne (client/score-queue.ts)
 *  en a besoin pour distinguer « à retenter » (réseau, 401, 5xx) de « sans espoir » (400, 404) :
 *  sans cette distinction, une entrée invalide bloquerait la file pour toujours. `status` vaut null
 *  quand fetch lui-même a échoué (pas de réseau du tout). */
export class ApiError extends Error {
  constructor(message: string, readonly status: number | null) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Lit le message d'erreur français du serveur ({ error }), ou un repli générique. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: unknown }
    if (typeof data.error === 'string' && data.error.length > 0) return data.error
  } catch {
    // Corps non-JSON (page statique 404, erreur proxy...) : on retombe sur le repli.
  }
  return 'Une erreur est survenue. Réessaie.'
}

/** Session courante, ou null si non connecté (401). LÈVE sur panne réseau (fetch rejette)
 *  et sur toute autre erreur serveur : main.ts interprète ce rejet comme « hors ligne ». */
export async function apiMe(): Promise<MeResponse | null> {
  const res = await request('/me', 'GET')
  if (res.status === 401) return null
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as MeResponse
}

/** Connexion. LÈVE Error(message serveur) sur identifiants faux (401) ou blocage (429). */
export async function apiLogin(pseudo: string, password: string): Promise<MeResponse> {
  const res = await request('/login', 'POST', { pseudo, password })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as MeResponse
}

/** Inscription par code famille. LÈVE Error(message serveur) sur code/pseudo/mdp refusé. */
export async function apiRegister(
  pseudo: string,
  password: string,
  familyCode: string,
): Promise<MeResponse> {
  const res = await request('/register', 'POST', { pseudo, password, familyCode })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as MeResponse
}

/** Déconnexion. Idempotente côté serveur (204 même sans session) : un 401 n'est pas une erreur. */
export async function apiLogout(): Promise<void> {
  const res = await request('/logout', 'POST')
  if (!res.ok && res.status !== 401) throw new Error(await errorMessage(res))
}

/** Envoi d'un score (authentifié) : 204 attendu — SAUF si ce run accomplit un DÉFI DE CARTE pour
 *  la première fois (22/07) ou remporte un DUEL DIFFÉRÉ (25/07) : le serveur répond alors 200 avec
 *  ce qu'il y a à fêter (listes vides dans tous les autres cas). dash/torch = MODE de la partie jouée
 *  (records par mode, 16/07) — le serveur tient un record par combinaison. */
export async function apiSubmitScore(
  levelSlug: string,
  timeMs: number,
  deaths: number,
  dash: boolean,
  torch: boolean,
): Promise<ScoreResponse> {
  const res = await request('/scores', 'POST', { levelSlug, timeMs, deaths, dash, torch })
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status)
  // 204 = rien de spécial à fêter (le cas courant) : on rend des listes vides plutôt que null,
  // pour que l'appelant n'ait jamais à tester la présence d'un corps.
  if (res.status !== 200) return { challengesCompleted: [], duelsWon: [] }
  return (await res.json()) as ScoreResponse
}

/** Classement famille (authentifié). Utilisé par l'écran classement (Task 9). */
export async function apiLeaderboard(): Promise<LeaderboardResponse> {
  const res = await request('/leaderboard', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as LeaderboardResponse
}

/** Historique des parties en ligne (authentifié) : stats par joueur + derniers matchs.
 *  Utilisé par la section « En ligne » du classement (lot 4b). */
export async function apiMatches(): Promise<MatchesResponse> {
  const res = await request('/matches', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as MatchesResponse
}

// --- La Mine aux briques (Lot A) : économie (pièces) + quêtes -----------------
// Même contrat : réponse non 2xx -> LÈVE Error(message serveur, en français). Les deux routes
// sont derrière requireAuth (401 si déconnecté) : l'appelant enrobe en try/catch et dégrade en
// silence (accueil sans badge, aucune progression enregistrée) hors ligne ou non connecté.

/** Le FANTÔME à battre sur une carte, dans le mode demandé (25/07). null = aucun fantôme (204 :
 *  carte jamais finie, ou seuls des enregistrements d'une version périmée du moteur) — le client
 *  joue alors sans fantôme, sans message. LÈVE seulement sur erreur serveur ou panne réseau. */
/** Le fantôme à battre. `rival` = le pseudo choisi dans les Réglages, '' pour le choix automatique
 *  (le plus rapide des autres). La lampe n'entre plus dans le choix : elle ne change pas le rejeu. */
export async function apiGhost(levelSlug: string, dash: boolean, rival = ''): Promise<GhostResponse | null> {
  const q = `?levelSlug=${encodeURIComponent(levelSlug)}&dash=${dash ? 1 : 0}` +
    (rival ? `&rival=${encodeURIComponent(rival)}` : '')
  const res = await request('/ghost' + q, 'GET')
  if (res.status === 204) return null
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as GhostResponse
}

/** Les joueurs qu'on peut désigner comme adversaire, du plus présent au moins présent. */
export async function apiGhostRivals(): Promise<GhostRivalDto[]> {
  const res = await request('/ghost-rivals', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return ((await res.json()) as GhostRivalsResponse).rivals
}

/** Dépose l'enregistrement d'une course (25/07). « Tirer et oublier » : le serveur ne la garde que
 *  si elle correspond au record personnel du joueur, et répond 204 dans tous les cas. */
export async function apiPutGhost(
  levelSlug: string, dash: boolean, torch: boolean, timeMs: number, seed: number, ticks: string,
): Promise<void> {
  const res = await request('/ghost', 'POST', { levelSlug, dash, torch, timeMs, seed, ticks })
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Mes défis reçus encore ouverts + les pseudos de la famille (25/07, duel différé). */
export async function apiDuels(): Promise<DuelsResponse> {
  const res = await request('/duels', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as DuelsResponse
}

/** Lance un défi : « bats mon temps sur cette carte, dans ce mode » (25/07). La cible n'est PAS
 *  envoyée — le serveur prend le meilleur temps de l'expéditeur, donc on ne peut pas défier
 *  quelqu'un de battre un temps qu'on n'a pas fait. LÈVE Error(message serveur) sur refus. */
export async function apiCreateDuel(toPseudo: string, levelSlug: string, dash: boolean, torch: boolean): Promise<void> {
  const res = await request('/duels', 'POST', { toPseudo, levelSlug, dash, torch })
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Journal « pendant ton absence » (25/07). ⚠️ L'appeler CONSOMME la fenêtre côté serveur :
 *  un seul appel par affichage d'accueil, jamais en boucle. */
export async function apiJournal(): Promise<JournalResponse> {
  const res = await request('/journal', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as JournalResponse
}

/** État de la Mine (authentifié) : solde du portefeuille + quêtes du jour/semaine/vie avec progression,
 *  + inventaire (loot_ids possédés) et équipement courant (slot -> loot_id) depuis le Lot C. */
export async function apiMineState(): Promise<MineStateResponse> {
  const res = await request('/mine/state', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as MineStateResponse
}

/** Les cartes officielles retouchées par l'admin (lecture PUBLIQUE : tout le monde doit voir la
 *  même carte, sinon les chronos ne se comparent plus). */
export async function apiLevelOverrides(): Promise<{ overrides: OverrideDto[] }> {
  const res = await request('/level-overrides', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as { overrides: OverrideDto[] }
}

/** ADMIN : remplace une carte officielle. Le serveur refuse une grille invalide. */
export async function apiPutOfficialLevel(
  slug: string, ascii: string, world: string, monkSpeed: string,
  smartMonks: boolean, monkPowers: boolean, decor: string,
): Promise<void> {
  const res = await request(`/level-official/${encodeURIComponent(slug)}`, 'PUT',
    { ascii, world, monkSpeed, smartMonks, monkPowers, decor })
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status)
}

/** ADMIN : rend la carte d'origine du catalogue. */
export async function apiDropOfficialLevel(slug: string): Promise<void> {
  const res = await request(`/level-official/${encodeURIComponent(slug)}`, 'DELETE')
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status)
}

/** LE PARCOURS (30/07) : la carte AVEC l'état de ce joueur — un état déjà tranché par le serveur
 *  pour chaque nœud (fini / courant / clé / verrouillé). Le client n'en recalcule aucun. */
export async function apiParcours(): Promise<ParcoursDto> {
  const res = await request('/parcours', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as ParcoursDto
}

/** Signale un échec de plus sur un niveau du parcours (alimente la clé de passage).
 *  Best-effort : un échec perdu ne casse rien, il retarde juste la clé d'un essai. */
export async function apiParcoursEchec(slug: string): Promise<void> {
  await request('/parcours/echec', 'POST', { slug }).catch(() => undefined)
}

/** Équipe (lootId = id d'un loot possédé du bon slot) ou déséquipe (lootId = null) un cosmétique
 *  (authentifié, Lot C). Renvoie l'équipement complet après l'opération (slot -> loot_id). LÈVE
 *  Error(message serveur) sur refus (slot inconnu / loot non possédé / mauvais slot → 400 ; 401). */
export async function apiEquip(slot: string, lootId: string | null): Promise<EquipResponse> {
  const res = await request('/mine/equip', 'POST', { slot, lootId })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as EquipResponse
}

/** Achète `n` briques (entier 1..99) à 100 pièces l'unité (authentifié, Lot B). Renvoie le wallet
 *  frais. LÈVE Error(message serveur) sur solde insuffisant (400) ou déconnexion (401). */
export async function apiBuyBricks(n: number): Promise<{ wallet: WalletDto }> {
  const res = await request('/mine/buy-bricks', 'POST', { n })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as { wallet: WalletDto }
}

/** Casse UNE brique (authentifié, Lot B). PROFONDEUR (21/07) : `depth` (1..3) = l'étage creusé —
 *  le serveur débite DEPTH_COST[depth-1] briques et multiplie pièces/éclats par DEPTH_MULT ; le
 *  tirage de rareté reste identique partout. LÈVE Error(message serveur) sans briques (400) ou
 *  déconnexion (401). */
export async function apiCrack(depth: 1 | 2 | 3 = 1): Promise<CrackResponse> {
  const res = await request('/mine/crack', 'POST', { depth })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as CrackResponse
}

/** Achète directement un cosmétique NON possédé à la boutique d'éclats (authentifié, Lot B). Renvoie
 *  le wallet frais. LÈVE Error(message serveur) sur objet indisponible / déjà possédé / éclats
 *  insuffisants (400) ou déconnexion (401). */
export async function apiShardBuy(lootId: string): Promise<{ wallet: WalletDto }> {
  const res = await request('/mine/shard-buy', 'POST', { lootId })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as { wallet: WalletDto }
}

/** Active un consommable POSSÉDÉ (authentifié, Lot B) : ex. « Filon ×2 » arme le boost 24 h. Renvoie
 *  le wallet frais. LÈVE Error(message serveur) sur objet non utilisable / non possédé (400) ou 401. */
export async function apiUse(lootId: string): Promise<{ wallet: WalletDto }> {
  const res = await request('/mine/use', 'POST', { lootId })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as { wallet: WalletDto }
}

/** Progression des quêtes + crédits solo en fin de niveau (authentifié). counters = compteurs
 *  accumulés pendant la partie ; finished/noDeath/beatRecord décrivent la fin. Le serveur est la
 *  SEULE autorité qui crédite ; renvoie le wallet frais + les quêtes complétées (pour les toasts). */
export async function apiQuestProgress(payload: {
  counters: Partial<QuestCounters>
  finished?: boolean
  noDeath?: boolean
  beatRecord?: boolean
}): Promise<QuestProgressResponse> {
  const res = await request('/quests/progress', 'POST', payload)
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as QuestProgressResponse
}

// --- Cartes de l'éditeur (lot 3) : CRUD des niveaux famille -----------------
// Même contrat que ci-dessus : réponse non 2xx -> LÈVE Error(message serveur, en
// français) pour affichage direct dans l'UI. Les routes 204 (publish/finished/
// delete) ne renvoient pas de corps : on vérifie seulement res.ok.

/** Mes cartes (brouillons + publiées), plus récentes d'abord. */
export async function apiMyLevels(): Promise<LevelSummary[]> {
  const res = await request('/levels/mine', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as LevelSummary[]
}

/** Bibliothèque famille : les cartes publiées de tous, GRILLE INCLUSE (vignettes, A3). */
export async function apiLibrary(): Promise<LevelFull[]> {
  const res = await request('/levels/library', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as LevelFull[]
}

/** Une carte complète (grille incluse) : la mienne ou une publiée, sinon 404 -> LÈVE. */
export async function apiGetLevel(id: number): Promise<LevelFull> {
  const res = await request('/levels/' + id, 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as LevelFull
}

/** Crée une carte, visible par tous dès l'enregistrement. Renvoie la carte complète (avec son id). */
export async function apiCreateLevel(name: string, ascii: string, world: WorldKey, monkSpeed: MonkSpeed, smartMonks: boolean, decor = '', monkPowers = false): Promise<LevelFull> {
  const res = await request('/levels', 'POST', { name, ascii, world, monkSpeed, smartMonks, decor, monkPowers })
  // ApiError (et pas Error) : la file des brouillons hors ligne a besoin du statut pour distinguer
  // « à retenter » (pas de réseau) de « sans espoir » (grille refusée), cf. client/offline.ts.
  if (!res.ok) throw new ApiError(await errorMessage(res), res.status)
  return (await res.json()) as LevelFull
}

/** Modifie name/ascii/monde/vitesse des moines/IA maligne/décor d'une carte (auteur seul). La carte garde son statut publié. */
export async function apiUpdateLevel(id: number, name: string, ascii: string, world: WorldKey, monkSpeed: MonkSpeed, smartMonks: boolean, decor = '', monkPowers = false): Promise<LevelFull> {
  const res = await request('/levels/' + id, 'PUT', { name, ascii, world, monkSpeed, smartMonks, decor, monkPowers })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as LevelFull
}

/** Publie (true) ou dépublie (false) une carte. 204 attendu. */
export async function apiPublishLevel(id: number, published: boolean): Promise<void> {
  const res = await request('/levels/' + id + (published ? '/publish' : '/unpublish'), 'POST')
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Marque une carte finie par son auteur (idempotent côté serveur). 204 attendu. */
export async function apiFinishedLevel(id: number): Promise<void> {
  const res = await request('/levels/' + id + '/finished', 'POST')
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Compte une partie lancée sur une carte famille (classement par fréquence, 18/07). Fire-and-forget :
 *  un simple compteur ne doit jamais bloquer ni faire échouer le démarrage d'une partie. */
export async function apiPlayLevel(id: number): Promise<void> {
  try { await request('/levels/' + id + '/play', 'POST') } catch { /* compteur best-effort : on ignore */ }
}

/** Enregistre personnage + teinte sur le COMPTE (skins 19/07). Silencieux hors-ligne :
 *  les prefs locales suffisent, le serveur ré-alignera à la prochaine connexion. */
export async function apiSetSkin(skin: string, hue: number): Promise<void> {
  try { await request('/me/skin', 'POST', { skin, hue }) } catch { /* hors-ligne : best-effort */ }
}

/** Supprime une carte (auteur ou admin). 204 attendu. */
export async function apiDeleteLevel(id: number): Promise<void> {
  const res = await request('/levels/' + id, 'DELETE')
  if (!res.ok) throw new Error(await errorMessage(res))
}

// --- Campagnes (lot campagne) : CRUD + score solo ----------------------------
// Même contrat : réponse non 2xx -> LÈVE Error(message serveur, en français). apiCampaignScore
// est FIRE-AND-FORGET (comme le solo) : on tire le temps de bout en bout et on l'oublie ; le
// serveur ne garde que le meilleur, un envoi non battu reste un 204.

/** Liste des campagnes (titres résolus, meilleur temps solo, total coop). */
export async function apiListCampaigns(): Promise<CampaignSummary[]> {
  const res = await request('/campaigns', 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as CampaignSummary[]
}

/** Une campagne complète (5 niveaux résolus, grilles incluses ; rows null si indisponible). */
export async function apiGetCampaign(id: number): Promise<CampaignFull> {
  const res = await request('/campaigns/' + id, 'GET')
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as CampaignFull
}

/** Crée une campagne (5 slugs ordonnés). Renvoie la campagne complète (avec son id). */
export async function apiCreateCampaign(
  name: string, levelSlugs: string[], entree: string | null = null,
): Promise<CampaignFull> {
  const res = await request('/campaigns', 'POST', { name, levelSlugs, entree })
  if (!res.ok) throw new Error(await errorMessage(res))
  return (await res.json()) as CampaignFull
}

/** Change la récompense d'une campagne existante (auteur ou admin). null = plus de récompense. */
export async function apiSetCampaignEntree(id: number, entree: string | null): Promise<void> {
  const res = await request('/campaigns/' + id + '/entree', 'POST', { entree })
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Supprime une campagne (auteur ou admin). 204 attendu. */
export async function apiDeleteCampaign(id: number): Promise<void> {
  const res = await request('/campaigns/' + id, 'DELETE')
  if (!res.ok) throw new Error(await errorMessage(res))
}

/** Envoie le temps solo total (fire-and-forget) : 204 attendu, la base ne garde que le meilleur. */
export async function apiCampaignScore(id: number, totalMs: number): Promise<CampaignScoreResponse> {
  const res = await request('/campaigns/' + id + '/score', 'POST', { totalMs })
  if (!res.ok) throw new Error(await errorMessage(res))
  // La route répondait 204 ; elle renvoie désormais l'entrée en scène débloquée (ou null). Un
  // corps illisible ne doit PAS faire échouer l'envoi du temps : on retombe sur « rien gagné ».
  return (await res.json().catch(() => ({ entreeGagnee: null }))) as CampaignScoreResponse
}
